import { faCheck, faClose, faEdit, faPlus, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Stack } from "react-bootstrap";
import {
  deleteProgram,
  deleteProgramLevel,
  patchProgram,
  patchProgramLevel,
  postProgramLevel,
  reorderProgramLevels,
} from "../../../services/httpClient";
import Input from "../../ui/Input";
import { unsavedChanges } from "../../../App";
import IconEmbeddedButton from "../../ui/IconEmbeddedButton";
import ReorderEditDeleteCards from "../../ui/ReorderEditDeleteCards";

interface CardState {
  item: any;
  getItems: any;
  setOrgToSave: any;
  setErrMessage: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const ProgramCard = ({ item: initialItem, getItems, setOrgToSave, setErrMessage, setShowToast, setToastText, setToastBody }: CardState) => {
  const [item, setItem] = useState<any>(initialItem);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [progName, setProgName] = useState<string>(initialItem?.name);
  const [newLevel, setNewLevel] = useState<string>("");

  useEffect(() => {
    setItem(initialItem);
  }, [initialItem]);

  const onClickEdit = () => {
    setIsAdding(false);
    setIsEditing(true);
  };

  const onClickEditFinal = () => {
    const updatedProg = {
      name: progName,
    };

    patchProgram(updatedProg, item?.id)
      .then((res: any) => {
        unsavedChanges.value = false;
        setOrgToSave(false);
        setShowToast(true);
        setToastText("Program Updated");
        setToastBody(progName);
        setIsEditing(false);
        getItems(true);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelEdit = () => {
    unsavedChanges.value = false;
    setOrgToSave(false);
    setIsEditing(false);
    setErrMessage(undefined);
  };

  const onUpdateName = (e: any) => {
    setProgName(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
    setErrMessage(undefined);
  };

  const onClickDelete = () => {
    setIsDeleting(true);
  };

  const onClickDeleteFinal = () => {
    deleteProgram(item?.id)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Program Deleted");
        setToastBody(progName);
        setIsDeleting(false);
        getItems(true);
      })
      .catch((err: any) => {
        setIsDeleting(false);
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
    setErrMessage(undefined);
  };

  // Level adding
  const onClickAdd = () => {
    setIsAdding(true);
    unsavedChanges.value = true;
    setOrgToSave(true);
    setErrMessage(undefined);
  };

  const onUpdateLevel = (e: any) => {
    setNewLevel(e.target.value);
    unsavedChanges.value = true;
    setOrgToSave(true);
    setErrMessage(undefined);
  };

  const onClickAddFinal = () => {
    setErrMessage("");
    if (!newLevel || newLevel.trim() === "") {
      setErrMessage("Please enter a name for the program level.");
      return;
    }

    postProgramLevel(item.id, newLevel)
      .then((res: any) => {
        unsavedChanges.value = false;
        setOrgToSave(false);
        setShowToast(true);
        setToastText("Program Level Created");
        setToastBody(newLevel);
        setIsAdding(false);
        setNewLevel("");
        getItems(true);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelAdd = () => {
    unsavedChanges.value = false;
    setOrgToSave(false);
    setIsAdding(false);
    setErrMessage("");
    setNewLevel("");
  };

  // Level editing
  const onClickEditLevelFinal = (levelId: any, levelName: string) => {
    setErrMessage("");

    patchProgramLevel(levelId, levelName)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Program Level Updated");
        setToastBody(levelName);
        unsavedChanges.value = false;
        setOrgToSave(false);
        getItems(true);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  // Level deletion
  const onClickDeleteLevelFinal = (levelId: any, name: string) => {
    deleteProgramLevel(levelId)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Program Level Deleted");
        setToastBody(name);
        getItems(true);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  // Renders
  const renderAdd = () => {
    return (
      <>
        <Card key={item.name} className="mb-1 mt-2">
          <Card.Body>
            <Stack direction="horizontal" gap={3}>
              <Col>
                <Input label="Name" type="text" value={newLevel} hasFocus={true} onUpdate={(e: any) => onUpdateLevel(e)} />
              </Col>
              <FontAwesomeIcon className="text-muted me-1 cursor-pointer" icon={faCheck} onClick={() => onClickAddFinal()} />
              <div className="vr" />
              <FontAwesomeIcon className="text-muted me-1 cursor-pointer" icon={faClose} onClick={() => onClickCancelAdd()} />
            </Stack>
          </Card.Body>
        </Card>
        {item?.programLevels?.length > 0 && <hr></hr>}
      </>
    );
  };

  const renderEdit = () => {
    return (
      <Card key={item.name} className="mb-1">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Col>
              <Input label="Name" type="text" value={progName} hasFocus={true} onUpdate={(e: any) => onUpdateName(e)} />
            </Col>
            <FontAwesomeIcon className="text-muted me-1 cursor-pointer" icon={faCheck} onClick={() => onClickEditFinal()} />
            <div className="vr" />
            <FontAwesomeIcon className="text-muted me-1 cursor-pointer" icon={faClose} onClick={() => onClickCancelEdit()} />
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderDelete = () => {
    return (
      <Card key={item.name} className="mb-2">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} gap={2}>
              <Card.Subtitle>Remove {item?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-danger-color button" onClick={() => onClickDeleteFinal()}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const updateLevelOrder = (newOrder: any) => {
    reorderProgramLevels(newOrder)
      .then(() => {
        setShowToast(true);
        setToastText("Program Level Order");
        setToastBody("The level order of " + item?.name + " has been updated.");
        setItem({ ...item, programLevels: newOrder });
        // setDraggedIndex(null);
      })
      .catch((err) => {
        setErrMessage(err?.response?.data?.msg);
      });
  };

  return (
    <div className="p-2">
      {isDeleting ? (
        <>{renderDelete()}</>
      ) : (
        <>
          {isEditing ? (
            <>
              {renderEdit()}
              {item?.programLevels?.length > 0 && <hr className="mb-3 mt-3"></hr>}
            </>
          ) : (
            <Row>
              <Col xs={9}>
                <h6 className="ms-1 mt-1" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {item.name}
                </h6>
              </Col>
              <Col xs={3} className="d-flex justify-content-end pe-3">
                <Stack direction="horizontal" gap={2} className="mb-2">
                  <IconEmbeddedButton onClickButton={onClickAdd} tooltipText={"Add Level"} icon={faPlus} />
                  <div className="vr" />
                  <IconEmbeddedButton onClickButton={onClickEdit} tooltipText={"Edit Program"} icon={faEdit} />
                  <div className="vr" />
                  <IconEmbeddedButton onClickButton={onClickDelete} tooltipText={"Delete Program"} icon={faTrash} />
                </Stack>
              </Col>
            </Row>
          )}
          {isAdding && <>{renderAdd()}</>}
          {item?.programLevels?.length > 0 && (
            <ReorderEditDeleteCards
              items={item?.programLevels}
              onEditFinal={onClickEditLevelFinal}
              onDeleteFinal={onClickDeleteLevelFinal}
              updateOrder={updateLevelOrder}
              setErrMessage={setErrMessage}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ProgramCard;
